import { type FC } from "react";

import type {
  RepositoryIndexingRun,
  RepositoryIndexingRunFailure,
} from "../../types";

import { formatTimestamp } from "../../utils/sourceControlIndexing";
import { Badge } from "../common/Badge";
import { DataState } from "../common/DataState";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";

interface Props {
  selectedRun: RepositoryIndexingRun | null;
  failures: RepositoryIndexingRunFailure[];
  loading: boolean;
  error: string | null;
  repositorySlug?: string | null;
}

function getEmptyDescription(
  selectedRun: RepositoryIndexingRun | null,
  repositorySlug?: string | null,
) {
  if (selectedRun === null) {
    return "Choose an indexing run to inspect repository-level failures.";
  }
  if (repositorySlug) {
    return `No failures are recorded for ${repositorySlug} in this run.`;
  }
  return "This run has no repository failures recorded.";
}

const FailureSummaryCard: FC<{
  failureCount: number;
  selectedRun: RepositoryIndexingRun;
}> = ({ failureCount, selectedRun }) => {
  const headerBadges = [
    <StatusBadge key="status" status={selectedRun.status} />,
    <Badge
      key="failed-repos"
      variant={failureCount > 0 ? "warning" : "success"}
      size="sm"
    >
      {failureCount} failed repos
    </Badge>,
  ];

  const metaBadges = [
    <Badge key="failed-items" variant="status" size="sm">
      {selectedRun.item_counts.failed} failed items
    </Badge>,
  ];

  if (selectedRun.completed_at) {
    metaBadges.push(
      <Badge key="completed" variant="status" size="sm">
        Completed {formatTimestamp(selectedRun.completed_at)}
      </Badge>,
    );
  } else if (selectedRun.updated_at) {
    metaBadges.push(
      <Badge key="updated" variant="status" size="sm">
        Updated {formatTimestamp(selectedRun.updated_at)}
      </Badge>,
    );
  }

  return (
    <div className="rounded-none border border-slate-300 bg-slate-50/70 px-4 py-3 dark:border-slate-700 dark:bg-arti-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
              {selectedRun.run_type}
            </p>
            {headerBadges}
          </div>

          <div className="flex flex-wrap items-center gap-2">{metaBadges}</div>
        </div>

        <Text className="max-w-sm text-slate-500 dark:text-slate-400">
          Use “Select failed repos from the latest run” in the trigger dialog to
          retry only the repositories that failed here.
        </Text>
      </div>
    </div>
  );
};

const FailureListItem: FC<{ failure: RepositoryIndexingRunFailure }> = ({
  failure,
}) => {
  const errorMessage = failure.error_message ?? "No error message captured.";
  const metadata = [
    { label: "Run Item", value: failure.run_item_id },
    ...(failure.task_id ? [{ label: "Task", value: failure.task_id }] : []),
  ];

  return (
    <li className="bg-white px-4 py-3 dark:bg-transparent">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
              {failure.repository_slug}
            </p>
            <Badge variant="warning" size="sm">
              failed
            </Badge>
          </div>

          <div className="rounded-none border border-amber-200 bg-amber-50/70 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10">
            <p
              className="whitespace-pre-wrap break-words font-mono text-[11px] text-amber-800 dark:text-amber-200"
              title={errorMessage}
            >
              {errorMessage}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1 text-right">
          {metadata.map((item) => (
            <div key={item.label} className="space-y-1">
              <Text variant="caption">{item.label}</Text>
              <span className="block font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </li>
  );
};

export const RepositoryIndexingFailuresPanel: FC<Props> = ({
  selectedRun,
  failures,
  loading,
  error,
  repositorySlug,
}) => {
  const failureCount = failures.length;
  const isEmpty = selectedRun !== null && !loading && failureCount === 0;
  const scopeLabel = repositorySlug
    ? `Scoped to ${repositorySlug}`
    : "Across all repositories in this run";

  return (
    <PanelSection
      headerContent={
        <PanelHeaderBlock
          eyebrow={repositorySlug ? "Scoped Diagnostics" : "Run Diagnostics"}
          title="Failure Details"
          description={scopeLabel}
        />
      }
    >
      <DataState
        loading={loading}
        error={error}
        empty={selectedRun === null || isEmpty}
        emptyTitle={
          selectedRun === null ? "Select a run first" : "No failed repositories"
        }
        emptyDescription={getEmptyDescription(selectedRun, repositorySlug)}
      >
        {selectedRun && (
          <div className="space-y-4">
            <FailureSummaryCard
              failureCount={failureCount}
              selectedRun={selectedRun}
            />

            <div className="custom-scrollbar max-h-[28rem] overflow-y-auto rounded-none border border-slate-300 dark:border-slate-700">
              <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {failures.map((failure) => (
                  <FailureListItem
                    key={failure.run_item_id}
                    failure={failure}
                  />
                ))}
              </ul>
            </div>
          </div>
        )}
      </DataState>
    </PanelSection>
  );
};
